import type {
  CustomCapacity,
  CustomCapacityLevel,
  CustomEnemySheet,
  CustomEntity,
  CustomManoeuvre,
  CustomTrainingStatus,
} from "../../api/types";
import { k, t } from "../../i18n/i18n";

/** Fiche doctrinale d'un ENI personnalisé (registre A135) : choix proposés, libellés (clés françaises traduites à
 *  l'affichage) et contrôles de chaque étape, les mêmes que ceux du serveur (`custom_me.normalize_sheet`). */

export const QUOTA = 5;
export const MAX_DESCRIPTION = 1500;
export const MAX_STRENGTHS = 1500;
export const MAX_DOCUMENTS = 5;
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
export const REFERENCE_CODES = ["TANTALE", "TITAN", "TITANE", "DEIMOS", "MERCURE"];
/** Mots des ordres refusés comme nom de code (points cardinaux, désignations d'un camp) : liste du serveur
 *  (`custom_me.RESERVED_CODES`). Un ENI déjà nommé ainsi garde son nom. */
export const RESERVED_CODES = [
  "NORD", "SUD", "EST", "OUEST", "NORD-EST", "NORD-OUEST", "SUD-EST", "SUD-OUEST",
  "NORTH", "SOUTH", "EAST", "WEST", "NORTH-EAST", "NORTH-WEST", "SOUTH-EAST", "SOUTH-WEST",
  "NORDEST", "NORDOUEST", "SUDEST", "SUDOUEST", "NORTHEAST", "NORTHWEST", "SOUTHEAST", "SOUTHWEST",
  "ENI", "EN", "AMI", "AMIS", "ENNEMI", "ENNEMIS", "ENEMY", "FRIEND", "FORAD", "OPFOR",
  "NEUTRE", "NEUTRES", "INCONNU", "INCONNUS", "HOSTILE", "NEUTRAL", "UNKNOWN",
  "ROUGE", "BLEU", "BLEUE", "VERT", "VERTE", "ORANGE", "JAUNE", "NOIR", "NOIRE", "BLANC", "BLANCHE",
  "GRIS", "GRISE", "RED", "BLUE", "GREEN", "YELLOW", "BLACK", "WHITE", "GREY", "GRAY",
];
export const CODE_PATTERN = /^[A-Z][A-Z0-9-]{1,23}$/;

type Choice<T extends string> = { value: T; label: string; detail?: string };

export const INSPIRATIONS: Choice<CustomEnemySheet["identite"]["inspiration"]>[] = [
  { value: "conventionnel", label: k("Armée conventionnelle"), detail: k("Forces étatiques organisées") },
  { value: "hybride", label: k("Force hybride"), detail: k("Moyens militaires et irréguliers mêlés") },
  { value: "irregulier", label: k("Force irrégulière"), detail: k("Groupes armés, milices, guérilla") },
  { value: "fictif", label: k("Force fictive"), detail: k("Sans modèle réel") },
];
/** Formules d'entité (contrat de `custom_me.ENTITIES`) : mot français (clé traduite à l'affichage) et mot anglais
 *  des documents, quelle que soit la langue de l'interface. */
export const ENTITIES: { value: CustomEntity; label: string; en: string }[] = [
  { value: "alliance", label: k("Alliance"), en: "Alliance" },
  { value: "confederation", label: k("Confédération"), en: "Confederation" },
  { value: "coalition", label: k("Coalition"), en: "Coalition" },
  { value: "union", label: k("Union"), en: "Union" },
  { value: "ligue", label: k("Ligue"), en: "League" },
  { value: "pacte", label: k("Pacte"), en: "Pact" },
  { value: "front", label: k("Front"), en: "Front" },
  { value: "mouvement", label: k("Mouvement"), en: "Movement" },
  { value: "organisation", label: k("Organisation"), en: "Organization" },
  { value: "reseau", label: k("Réseau"), en: "Network" },
];
/** Formule proposée d'après l'inspiration (`custom_me.SUGGESTED_ENTITY`). */
export const SUGGESTED_ENTITY: Record<CustomEnemySheet["identite"]["inspiration"], CustomEntity> = {
  conventionnel: "alliance",
  hybride: "ligue",
  irregulier: "front",
  fictif: "coalition",
};

/** Formule en vigueur : celle choisie, sinon celle que propose l'inspiration. */
export function entityKey(identite: CustomEnemySheet["identite"]): CustomEntity {
  const chosen = identite.entite;
  return chosen && ENTITIES.some((e) => e.value === chosen) ? chosen : (SUGGESTED_ENTITY[identite.inspiration] ?? "coalition");
}

/** Nom de l'entité dans les intitulés des documents : « ALLIANCE BOREAL » / « THE BOREAL ALLIANCE » (`entity_name`). */
export function entityName(identite: CustomEnemySheet["identite"], code: string, language: "fr" | "en"): string {
  const entity = ENTITIES.find((e) => e.value === entityKey(identite))!;
  const shown = code.trim().toUpperCase() || "…";
  return language === "en" ? `THE ${shown} ${entity.en.toUpperCase()}` : `${entity.label.toUpperCase()} ${shown}`;
}

export const TYPES_DOMINANTS: Choice<CustomEnemySheet["forces"]["type_dominant"]>[] = [
  { value: "blinde_mecanise", label: k("Blindé et mécanisé"), detail: k("Chars et infanterie sur engins blindés") },
  { value: "motorise", label: k("Motorisé"), detail: k("Infanterie sur véhicules à roues") },
  { value: "infanterie_legere", label: k("Infanterie légère"), detail: k("Combat à pied, peu de véhicules") },
  { value: "aeroporte", label: k("Aéroporté"), detail: k("Mis à terre par air") },
  { value: "hybride", label: k("Hétérogène"), detail: k("Forces de natures différentes mêlées") },
];
export const NIVEAUX_TECHNO: Choice<CustomEnemySheet["forces"]["niveau_techno"]>[] = [
  { value: "ancien", label: k("Ancien") },
  { value: "intermediaire", label: k("Intermédiaire") },
  { value: "moderne", label: k("Moderne") },
];
export const CAPACITIES: Choice<CustomCapacity>[] = [
  { value: "artillerie_lrm", label: k("Artillerie et lance-roquettes") },
  { value: "drones", label: k("Drones") },
  { value: "guerre_electronique", label: k("Guerre électronique") },
  { value: "sol_air", label: k("Défense sol-air") },
  { value: "appui_aerien", label: k("Appui aérien") },
  { value: "genie_franchissement", label: k("Génie : franchissement") },
  { value: "genie_obstacles", label: k("Génie : obstacles et mines") },
  { value: "logistique", label: k("Logistique") },
  { value: "nrbc", label: k("NRBC") },
  { value: "forces_speciales", label: k("Forces spéciales") },
  { value: "milices", label: k("Milices") },
  { value: "action_informationnelle", label: k("Action informationnelle") },
];
export const CAPACITY_LEVELS: Choice<CustomCapacityLevel>[] = [
  { value: "absent", label: k("Absente") },
  { value: "faible", label: k("Faible") },
  { value: "moyen", label: k("Moyenne") },
  { value: "fort", label: k("Forte") },
];
export const MANOEUVRES: Choice<CustomManoeuvre>[] = [
  { value: "frontale", label: k("Attaque frontale") },
  { value: "debordement", label: k("Débordement") },
  { value: "infiltration", label: k("Infiltration") },
  { value: "enveloppement_vertical", label: k("Enveloppement vertical") },
];
type Doctrine = CustomEnemySheet["doctrine"];
export const DOCTRINE_CHOICES: {
  [K in "commandement" | "echelonnement" | "feux" | "pertes" | "defense" | "contre_attaque" | "population"]: {
    label: string;
    options: Choice<Doctrine[K]>[];
  };
} = {
  commandement: {
    label: k("Commandement"),
    options: [
      { value: "centralise", label: k("Centralisé") },
      { value: "intention", label: k("Fondé sur l'intention") },
    ],
  },
  echelonnement: {
    label: k("Échelonnement"),
    options: [
      { value: "deux_echelons", label: k("Deux échelons") },
      { value: "echelon_reserve", label: k("Un échelon et une réserve") },
      { value: "disperse", label: k("Dispersé") },
    ],
  },
  feux: {
    label: k("Emploi des feux"),
    options: [
      { value: "massifs_preparatoires", label: k("Massifs et préparatoires") },
      { value: "precis_a_la_demande", label: k("Précis, à la demande") },
    ],
  },
  pertes: {
    label: k("Acceptation des pertes"),
    options: [
      { value: "faible", label: k("Faible") },
      { value: "moyenne", label: k("Moyenne") },
      { value: "forte", label: k("Forte") },
    ],
  },
  defense: {
    label: k("Défense"),
    options: [
      { value: "zone", label: k("De zone") },
      { value: "mobile", label: k("Mobile") },
      { value: "ferme", label: k("Ferme") },
    ],
  },
  contre_attaque: {
    label: k("Contre-attaque"),
    options: [
      { value: "immediate", label: k("Immédiate") },
      { value: "planifiee", label: k("Planifiée") },
      { value: "tardive", label: k("Tardive") },
    ],
  },
  population: {
    label: k("Population"),
    options: [
      { value: "ignoree", label: k("Ignorée") },
      { value: "exploitee", label: k("Exploitée") },
      { value: "boucliers", label: k("Employée comme bouclier") },
    ],
  },
};
export const SCALES: { key: "tempo" | "choc_usure" | "obstacles"; label: string; low: string; high: string }[] = [
  { key: "tempo", label: k("Tempo"), low: k("Méthodique"), high: k("Très rapide") },
  { key: "choc_usure", label: k("Choc ou usure"), low: k("Choc"), high: k("Usure") },
  { key: "obstacles", label: k("Emploi des obstacles"), low: k("Rare"), high: k("Systématique") },
];

/** Fiche d'un nouvel ENI : valeurs médianes (celles du serveur). */
export function defaultSheet(code = ""): CustomEnemySheet {
  return {
    identite: { code, description: "", inspiration: "conventionnel", entite: "" },
    forces: { odb_reference: "", type_dominant: "blinde_mecanise", niveau_techno: "intermediaire" },
    capacites: Object.fromEntries(CAPACITIES.map((c) => [c.value, "moyen"])) as Record<CustomCapacity, CustomCapacityLevel>,
    doctrine: {
      commandement: "centralise",
      echelonnement: "deux_echelons",
      tempo: 3,
      choc_usure: 3,
      manoeuvre_preferee: ["frontale"],
      feux: "massifs_preparatoires",
      pertes: "moyenne",
      defense: "zone",
      obstacles: 3,
      contre_attaque: "planifiee",
      profondeur: false,
      population: "ignoree",
    },
    forces_faiblesses: { points_forts: "", vulnerabilites: "" },
  };
}

/** Étapes de l'assistant : les cinq de la fiche, puis les modes d'action et la validation. */
export const STEPS = [
  { key: "identite", label: k("Identité") },
  { key: "forces", label: k("Forces") },
  { key: "capacites", label: k("Capacités") },
  { key: "doctrine", label: k("Doctrine d'emploi") },
  { key: "forces_faiblesses", label: k("Points forts et vulnérabilités") },
  { key: "modes", label: k("Modes d'action") },
] as const;
export type StepKey = (typeof STEPS)[number]["key"];

/** Nom de code saisi : majuscules, chiffres et tirets, jamais celui d'un ENI de référence ni un mot des ordres
 *  (sauf le nom déjà enregistré, `saved`) ; message traduit. */
export function codeError(code: string, taken: string[] = [], saved: string | null = null): string | null {
  const value = code.trim().toUpperCase();
  if (!value) return t("Saisissez un nom de code.");
  if (!CODE_PATTERN.test(value)) return t("2 à 24 lettres majuscules, chiffres ou tirets, en commençant par une lettre.");
  if (REFERENCE_CODES.includes(value)) return t("« {code} » est le nom d'un ENI de référence : choisissez-en un autre.", { code: value });
  if (RESERVED_CODES.includes(value) && value !== saved)
    return t("« {code} » est un mot des ordres (point cardinal, couleur ou désignation d'un camp) : choisissez-en un autre.", { code: value });
  if (taken.includes(value)) return t("Un ENI personnalisé s'appelle déjà « {code} » dans votre licence.", { code: value });
  return null;
}

/** Ce qui manque à une étape (traduit), null si elle est complète. */
export function stepError(step: StepKey, sheet: CustomEnemySheet, code: string, taken: string[] = [], saved: string | null = null): string | null {
  if (step === "identite") {
    return codeError(code, taken, saved) ?? (sheet.identite.description.length > MAX_DESCRIPTION
      ? t("Description : {n} caractères au plus.", { n: MAX_DESCRIPTION })
      : null);
  }
  if (step === "doctrine") {
    const moves = sheet.doctrine.manoeuvre_preferee.length;
    return moves < 1 || moves > 2 ? t("Choisissez une ou deux manœuvres préférées.") : null;
  }
  if (step === "forces_faiblesses") {
    const ff = sheet.forces_faiblesses;
    return ff.points_forts.length > MAX_STRENGTHS || ff.vulnerabilites.length > MAX_STRENGTHS
      ? t("Points forts et vulnérabilités : {n} caractères au plus chacun.", { n: MAX_STRENGTHS })
      : null;
  }
  return null;
}

/** Statuts du modèle de l'ENI en préparation (points d'attente animés) ou en affinage. */
export const PREPARING: CustomTrainingStatus[] = ["quick_queued", "quick_training"];
export const REFINING: CustomTrainingStatus[] = ["full_queued", "training", "paused", "evaluating"];

/** Libellés de l'état du modèle de l'ENI (clés françaises que rend le serveur, déclarées ici pour la traduction). */
export const TRAINING_LABELS = [
  k("Modèle de l'ENI non préparé"),
  k("Modèle prêt à l'essai"),
  k("Affinage en cours (quand le serveur est disponible)"),
  k("Modèle complet prêt"),
  k("Échec de la préparation du modèle de l'ENI"),
];
